/**
 * The dryland arm swing: the drill this app was built around, now expressed as
 * an exercise definition rather than wired straight into the render loop.
 *
 * Nothing about the measurement changed in being moved here. The counter is
 * still `RepCounter`, which accumulates the signed arm angle because this swing
 * genuinely revolves (see metrics/revolutions.js), and the criteria are still
 * the six in metrics/posture.js plus the two tempo rows. What the definition
 * adds is the declaration a second exercise needs: which view the criteria are
 * honest in, which thresholds they read, and what to say when one fails.
 *
 * This is the only calibrated exercise. Every threshold behind it was measured
 * off real footage and is recorded in docs/arm-swing-posture.md.
 */
import { RepCounter } from "../metrics/revolutions";
import { scoreRep } from "../metrics/posture";
import { scoreSpeed, scoreTempo } from "../metrics/tempo";

export const armSwing = {
  id: "arm_swing",
  name: "Arm swing",
  /** One line in the menu: what the body does, not what is measured. */
  blurb: "Full circles, on the toes, knees bent",
  /**
   * Every criterion is a sagittal-plane measurement, so from the front they are
   * either invisible or meaningless. Picking the exercise sets the view.
   */
  view: "side",
  /**
   * True only where the thresholds come from footage rather than judgement. It
   * is shown in the menu and printed on the receipt, because a number scored
   * against a guessed threshold looks exactly like one scored against a
   * measured threshold.
   */
  calibrated: true,
  /** Thresholds the criteria read, by their key in `STATE.posture`. */
  thresholdKeys: [
    "tiptoeMin",
    "kneeFlexMax",
    "trunkRockMax",
    "aheadOfKneeMax",
    "neckOffset",
    "neckTolerance",
    "tempoEvenBand",
    "swingSpeedMin",
  ],
  /**
   * Imperative and short, because it is read mid-rep from across a room — not a
   * restatement of the measurement. "Head level" rather than "eyes forward":
   * what is measured is head pitch, and where the athlete is looking is not
   * something the landmarks can report.
   */
  cues: {
    tiptoe: "Up on your toes",
    knee_flex: "Bend your knees",
    trunk_steady: "Tighten your core",
    head_back: "Head back",
    chest_back: "Chest back",
    neck_neutral: "Head level",
    swing_speed: "Swing faster",
  },
  /**
   * The tiptoe criterion is normalised by foot length measured over the whole
   * session, so the loop has to keep that scale; see index.js.
   */
  needsFootScale: true,
  createCounter: (thresholds) => new RepCounter(thresholds),
  scoreRep: (samples, { side, thresholds, footScale }) => [
    ...scoreRep(samples, thresholds, footScale),
    scoreSpeed(samples, side, thresholds),
    // Last because it is the only row that judges nothing: it reports which way
    // the swing leans and never fails a rep.
    scoreTempo(samples, side, thresholds),
  ],
};
